import { motion, useReducedMotion } from "framer-motion";
import { pctLabel } from "../lib/format";
import { DUR, EASE_OUT } from "../lib/motion";

/**
 * Classifier bands from backend/detection/injection.py, in ratio order.
 *
 * Everything on this scale is labelled in percent, including the thresholds,
 * because the marker is. Mixing a `0.98` axis with a `12.5%` marker asks the
 * reader to convert units to work out which side of the threshold they are on.
 *
 * The 0% tick is what makes the axis readable: without it the labels bunch into
 * the right third and the bar looks like it spans 70% to 98%, which puts a low
 * score in an inexplicable place. 100% is left off because it collides with 98%.
 *
 * Band widths are set in CSS (`.scale__band--*`) so the narrow clean band above
 * the threshold stays visible instead of collapsing to a sliver.
 */
const TICKS = [
  { at: 0, label: "0%", align: "start" },
  { at: 0.7, label: "70%" },
  { at: 0.85, label: "85%" },
  { at: 0.98, label: "98%" },
];

const BANDS = [
  { key: "high", name: "High", range: "below 70%", swatch: "var(--rose-500)" },
  { key: "medium", name: "Medium", range: "70 – 85%", swatch: "var(--amber-500)" },
  { key: "low", name: "Low", range: "85 – 98%", swatch: "#d8dd52" },
  { key: "none", name: "Clean", range: "98% and above", swatch: "var(--green-500)" },
];

/**
 * Where a document's fingerprint agreement between both readings falls across the
 * bands, with the thresholds labelled.
 *
 * Doubles as the empty state for the injection page: the bands explain what the
 * check will report before any file has been analysed.
 */
function SeverityScale({ ratio, threshold = 0.98 }) {
  const prefersReducedMotion = useReducedMotion();
  const hasValue = Number.isFinite(ratio);
  const position = hasValue ? Math.min(Math.max(ratio, 0), 1) * 100 : 0;

  return (
    <div className="scale">
      <div className="scale__plot" data-marker={hasValue}>
        <div className="scale__bar" aria-hidden="true">
          <span className="scale__band scale__band--high" />
          <span className="scale__band scale__band--medium" />
          <span className="scale__band scale__band--low" />
          <span className="scale__band scale__band--none" />
        </div>
        {hasValue && (
          <motion.span
            className="scale__marker"
            data-label={pctLabel(ratio, 1)}
            aria-hidden="true"
            initial={{ left: prefersReducedMotion ? `${position}%` : "0%", opacity: 0 }}
            animate={{ left: `${position}%`, opacity: 1 }}
            transition={
              prefersReducedMotion
                ? { duration: 0 }
                : { duration: DUR.reveal * 1.4, ease: EASE_OUT, delay: 0.1 }
            }
          />
        )}
      </div>

      <div className="scale__axis" aria-hidden="true">
        {TICKS.map((tick) => (
          <span
            className="scale__tick"
            key={tick.label}
            data-align={tick.align}
            style={{ left: `${tick.at * 100}%` }}
          >
            {tick.label}
          </span>
        ))}
      </div>

      <ul className="scale__legend" style={{ listStyle: "none", padding: 0, margin: 0 }}>
        {BANDS.map((band) => (
          <li className="scale__legend-item" key={band.key}>
            <span
              className="scale__legend-swatch"
              style={{ background: band.swatch }}
              aria-hidden="true"
            />
            {band.name}
            <span className="scale__legend-range">{band.range}</span>
          </li>
        ))}
      </ul>

      <p className="meta">
        How much the text in the file matches the text on the page. Anything under{" "}
        <strong className="mono">{pctLabel(threshold, 0)}</strong> means something is hidden.
        {hasValue && (
          <>
            {" "}
            This document scored <strong className="mono">{pctLabel(ratio, 2)}</strong>.
          </>
        )}
      </p>
    </div>
  );
}

export default SeverityScale;
